import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { ManagementMediumClippingControllerService, ManagementMediumFilterRequest } from 'api-clipping';
import { TimeFieldComponent } from '../../../../shared/components/time-field/time-field.component';
import { UtilsService } from '../../../../shared/services/utils.service';
import { RecordingResponse, RecordingService } from '../../../../shared/services/recording.service';

const ZONE = 'Europe/Skopje';

interface MediumOption {
	id: string;
	name: string;
}

interface InputData {
	recording: RecordingResponse;
}

// Д1 "Уреди" - a data-entry correction on an existing edition (wrong channel picked, wrong
// date/time typed during manual upload), never a new recording. The server is the real gate
// (ManagementRecordingServiceImpl.updateRecording): locked while clips already exist for this
// edition or while it is still processing - this dialog just surfaces that message if it fires.
@Component({
	selector: 'app-edit-recording-dialog',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatDialogModule,
		MatButtonModule,
		MatFormFieldModule,
		MatSelectModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatIconModule,
		MatDividerModule,
		TranslateModule,
		TimeFieldComponent,
	],
	templateUrl: './edit-recording-dialog.component.html',
	styleUrl: './edit-recording-dialog.component.scss',
})
export class EditRecordingDialogComponent implements OnInit {
	form: FormGroup;
	mediums: MediumOption[] = [];
	saving = false;
	errorMessage: string | null = null;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: InputData,
		private fb: FormBuilder,
		private dialogRef: MatDialogRef<EditRecordingDialogComponent>,
		private apiMedium: ManagementMediumClippingControllerService,
		private recordingService: RecordingService,
		private utils: UtilsService,
		private translate: TranslateService
	) {
		const [date, startTime] = EditRecordingDialogComponent.toSkopjeDateAndTime(data.recording.scheduledStart);
		this.form = this.fb.group({
			mediumId: [data.recording.mediumId, [Validators.required]],
			scheduledDate: [date, [Validators.required]],
			startTime: [startTime, [Validators.required]],
		});
	}

	ngOnInit(): void {
		this.apiMedium
			.getMediumsPageableFilter({ types: [ManagementMediumFilterRequest.TypesEnum.TV] }, 0, 200)
			.pipe(take(1))
			.subscribe(page => {
				this.mediums = (page.content || [])
					.map(medium => ({
						id: medium.id,
						name: this.utils.getParameterByLanguageScript(medium, 'name') || medium.nameLatin || '',
					}))
					.sort((a, b) => a.name.localeCompare(b.name));
			});
	}

	submit(): void {
		if (this.form.invalid || this.saving) {
			return;
		}
		this.saving = true;
		this.errorMessage = null;

		const date: Date = this.form.value.scheduledDate;
		const y = date.getFullYear();
		const m = (date.getMonth() + 1).toString().padStart(2, '0');
		const d = date.getDate().toString().padStart(2, '0');
		const scheduledStart = `${y}-${m}-${d}T${this.form.value.startTime}`;

		this.recordingService
			.updateRecording(this.data.recording.id, this.form.value.mediumId, scheduledStart)
			.pipe(take(1))
			.subscribe({
				next: recording => {
					this.saving = false;
					this.dialogRef.close(recording);
				},
				error: (err: HttpErrorResponse) => {
					this.saving = false;
					this.errorMessage = err.error?.message || this.translate.instant('RECORDINGS.EDIT_DIALOG.ERROR');
				},
			});
	}

	cancel(): void {
		if (!this.saving) {
			this.dialogRef.close();
		}
	}

	// Reads the Skopje-local calendar day and "HH:mm" out of an ISO instant, for the date-picker
	// (a plain local Date, midnight) and the app-time-field (any minute, not just 5-min steps -
	// the real recording can have started at any minute).
	private static toSkopjeDateAndTime(iso: string): [Date, string] {
		const parts = new Intl.DateTimeFormat('en-CA', {
			timeZone: ZONE,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			hour12: false,
		}).formatToParts(new Date(iso));
		const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00';
		const date = new Date(Number(get('year')), Number(get('month')) - 1, Number(get('day')));
		// Some Intl implementations return "24" for midnight even with hour12:false.
		const hour = get('hour') === '24' ? '00' : get('hour');
		const startTime = `${hour}:${get('minute')}`;
		return [date, startTime];
	}
}
